import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { RouterPrompt } from 'components/RouterPrompt';
import { TEMPORARY } from 'helpers/constant';
import { groupByDateBucket, timeFormat } from 'helpers/date';
import useMobile from 'hooks/useMobile';
import { MoreHorizontal, NotebookText, PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Task } from 'types/reducers/task';

interface TaskListProps {
  onAddNew: () => void;
  onChange: (_task: Task) => void;
}

const TasksList = ({ onAddNew, onChange }: TaskListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks, current, processingStreamingChunkStatusTask } = useSelector((state) => state.task);
  const { data, total_page, current_page } = tasks;
  const { isMobile } = useMobile();

  const [recordDelete, setRecordDelete] = useState<Task>();
  const [recordEdit, setRecordEdit] = useState<Task>();
  const [selectTask, setSelectTask] = useState<Task>();

  const isStreaming = processingStreamingChunkStatusTask;

  const { isOpen: isOpenSidebar, onToggle: onToggleSidebar } = useDisclosure({
    defaultIsOpen: true,
  });

  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();
  // Warn-before-switch dialog: opened when switching tasks would discard unsaved file edits.
  const {
    isOpen: isOpenWarnDialog,
    onOpen: onOpenWarnDialog,
    onClose: onCloseWarnDialog,
  } = useDisclosure();
  const warnCancelRef = useRef<any>();

  // Group already-loaded tasks by date for display headers (UI-only).
  const buckets = useMemo(() => groupByDateBucket(data, (item) => item.last_updated), [data]);

  const bucketLabel = (bucket: { key: string; label: string }) => {
    switch (bucket.key) {
      case 'today':
        return t('chat.group.today', { defaultValue: 'Today' });
      case 'yesterday':
        return t('chat.group.yesterday', { defaultValue: 'Yesterday' });
      case 'previous7days':
        return t('chat.group.previous7days', { defaultValue: 'Previous 7 days' });
      case 'older':
        return t('chat.group.older', { defaultValue: 'Older' });
      default:
        return bucket.label;
    }
  };

  const selectedTaskId = current?.task_id;
  const isAddDisabled = data?.[0]?.type === TEMPORARY || isStreaming;
  const isChangingSelectedFiles =
    JSON.stringify(current?.selectedFiles) !== JSON.stringify(current?.initialSelectedFiles);

  const onShowMore = () => {
    dispatch(
      taskAction.getTasks({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (task: Task) => {
    setRecordDelete(task);
    onOpenDeleteDialog();
  };

  const onClickEdit = (task: Task) => {
    setRecordEdit(task);
    onOpenEditDialog();
  };

  const handleDelete = (task: Task) => {
    dispatch(
      taskAction.deleteTask({
        id: task.id,
      })
    );
  };

  const handleEdit = (updatedTask: Task & { new_name: string }) => {
    dispatch(
      taskAction.editTaskName({
        id: updatedTask.id,
        new_name: updatedTask.new_name,
      })
    );
  };

  const onChangeTask = (task: Task) => {
    if (isStreaming) {
      return;
    }
    if (isChangingSelectedFiles && current.type !== TEMPORARY && current.task_id !== task.id) {
      // Unsaved file edits → confirm before switching (previously this branch set state
      // but opened no dialog, so the click did nothing and the user was stuck).
      setSelectTask(task);
      onOpenWarnDialog();
    } else {
      onContinueChangeTask(task);
    }
  };

  const onContinueChangeTask = (task: Task) => {
    onChange(task);
  };

  const onConfirmChangeTask = () => {
    if (selectTask) {
      onContinueChangeTask(selectTask);
    }
    setSelectTask(undefined);
    onCloseWarnDialog();
  };

  useEffect(() => {
    if (current?.task_id) {
      dispatch(taskAction.getDetailTask(current));
    }
  }, [current?.task_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!isMobile && <RouterPrompt when={isChangingSelectedFiles} />}

      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('summary.deleteTask')}
        dataBody={t('summary.deleteTaskInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('summary.renameTask')}
        btnAction={t('summary.rename')}
      />

      {/* Warn before discarding unsaved file selection when switching summaries */}
      <AlertDialog
        isOpen={isOpenWarnDialog}
        leastDestructiveRef={warnCancelRef}
        onClose={onCloseWarnDialog}
        returnFocusOnClose={false}
      >
        <AlertDialogOverlay bg='blackAlpha.600' backdropFilter='blur(3px)'>
          <AlertDialogContent
            bg='bg.surfaceRaised'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='xl'
            shadow='ds_lg'
          >
            <AlertDialogHeader
              color='text.default'
              fontFamily='heading'
              fontSize='lg'
              fontWeight={600}
            >
              {t('summary.discardChangesTitle', { defaultValue: 'Discard unsaved changes?' })}
            </AlertDialogHeader>
            <AlertDialogBody color='text.muted' fontSize='sm'>
              {t('summary.discardChangesBody', {
                defaultValue:
                  'You have unsaved file changes. Switching will discard them. Continue?',
              })}
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button
                ref={warnCancelRef}
                onClick={onCloseWarnDialog}
                size='ds-md'
                variant='ds-ghost'
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={onConfirmChangeTask} size='ds-md' variant='ds-primary'>
                {t('common.continue', { defaultValue: 'Continue' })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* DS task panel — surface, border, ds_sm shadow (design-guidelines.md §4) */}
      <Flex
        pos='relative'
        direction='column'
        overflow='hidden'
        // Match Translator panel width (colSpan 3 of a repeat(12,1fr) grid): proportional
        // so Summary tracks Translator on every screen size instead of a fixed px. (#78760)
        w={{ base: '100%', lg: isOpenSidebar ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        minW={{ base: '100%', lg: isOpenSidebar ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        maxW={{ lg: isOpenSidebar ? 'calc((100vw - 440px) / 4 + 32px)' : '52px' }}
        h='100%'
        minH={0}
        bg={{ lg: 'bg.surface' }}
        borderWidth={{ lg: '1px' }}
        borderColor={{ lg: 'border.default' }}
        borderRadius={{ lg: 'lg' }}
        shadow={{ lg: isOpenSidebar ? 'ds_sm' : 'none' }}
        transition='width .12s ease-out'
      >
        {/* Header: title + new task + collapse toggle.
            Collapsed → stack vertically so the expand toggle stays visible at 72px. */}
        <Flex
          align='center'
          direction={isOpenSidebar ? 'row' : 'column'}
          flexShrink={0}
          gap={2}
          px={isOpenSidebar ? 4 : 1}
          pt={4}
          pb={isOpenSidebar ? 3 : 2}
        >
          {isOpenSidebar && (
            <Text flex={1} color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
              {t('summary.summaries')}
            </Text>
          )}
          <Tooltip label={t('common.addNew')} placement={isOpenSidebar ? 'bottom' : 'right'}>
            <IconButton
              aria-label={t('common.addNew')}
              icon={<Plus size={isOpenSidebar ? 17 : 19} />}
              isDisabled={isAddDisabled}
              onClick={onAddNew}
              size='ds-md'
              variant='ds-ghost'
            />
          </Tooltip>
          <Tooltip
            display={{ base: 'none', lg: 'block' }}
            label={isOpenSidebar ? t('common.close') : t('common.open')}
            placement='right'
          >
            <IconButton
              display={{ base: 'none', lg: 'inline-flex' }}
              aria-label={isOpenSidebar ? t('common.close') : t('common.open')}
              icon={isOpenSidebar ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
              onClick={onToggleSidebar}
              size='ds-md'
              variant='ds-ghost'
            />
          </Tooltip>
        </Flex>

        {/* List */}
        <Box
          flex={1}
          overflowY={isOpenSidebar ? 'auto' : 'hidden'}
          px={isOpenSidebar ? 3 : 0}
          pb={3}
          visibility={isOpenSidebar ? 'visible' : 'hidden'}
        >
          {buckets.map((bucket) => (
            <Box key={bucket.key}>
              {/* Date-bucket header (TODAY / YESTERDAY / PREVIOUS 7 DAYS / month) */}
              <Text
                px={2}
                pt={2}
                pb={1}
                color='text.subtle'
                fontSize='xs'
                fontWeight={600}
                letterSpacing='0.04em'
                textTransform='uppercase'
              >
                {bucketLabel(bucket)}
              </Text>

              <Flex direction='column' gap='2px'>
                {bucket.items.map((item) => {
                  const isActive = selectedTaskId === item.id;
                  return (
                    <Flex
                      key={item.id}
                      align='flex-start'
                      gap={3}
                      px={3}
                      py={3}
                      bg={isActive ? 'primary.soft' : 'transparent'}
                      borderRadius='md'
                      _hover={{ bg: isActive ? 'primary.soft' : 'bg.canvas' }}
                      cursor={isStreaming ? 'not-allowed' : 'pointer'}
                      transition='background .12s ease-out'
                      onClick={() => {
                        onChangeTask(item);
                      }}
                    >
                      <Box
                        flexShrink={0}
                        mt='2px'
                        color={isActive ? 'primary.solid' : 'text.subtle'}
                      >
                        <NotebookText size={16} />
                      </Box>
                      <Box flex={1} minW={0}>
                        <Tooltip label={item.name} placement='top-start'>
                          <Text
                            overflow='hidden'
                            color={isActive ? 'primary.solid' : 'text.default'}
                            fontSize='sm'
                            fontWeight={isActive ? 600 : 500}
                            whiteSpace='nowrap'
                            textOverflow='ellipsis'
                          >
                            {item.name}
                          </Text>
                        </Tooltip>
                        <Text mt='2px' color='text.subtle' fontSize='xs'>
                          {timeFormat(item?.last_updated)}
                        </Text>
                      </Box>
                      <TaskOptions
                        data={item}
                        onClickDelete={onClickDelete}
                        onClickEdit={onClickEdit}
                      />
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          ))}

          {current_page + 1 < total_page && (
            <Button
              w='100%'
              mt={2}
              fontWeight={500}
              onClick={onShowMore}
              size='ds-sm'
              variant='ds-ghost'
            >
              {t('common.moreHistory')}
            </Button>
          )}
        </Box>
      </Flex>
    </>
  );
};

interface TaskOptionsProps {
  data: Task;
  onClickDelete: (_task: Task) => void;
  onClickEdit: (_task: Task) => void;
}

const TaskOptions = ({ data, onClickDelete, onClickEdit }: TaskOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu placement='bottom-end'>
      <MenuButton
        as={IconButton}
        flexShrink={0}
        aria-label={t('common.edit')}
        icon={<MoreHorizontal size={16} />}
        onClick={(e) => e.stopPropagation()}
        size='ds-sm'
        variant='ds-ghost'
      />
      <MenuList minW='140px' bg='bg.surfaceRaised' borderColor='border.default' shadow='ds_lg'>
        {data?.type !== TEMPORARY && (
          <MenuItem
            color='text.default'
            fontSize='sm'
            bg='transparent'
            _hover={{ bg: 'primary.soft', color: 'primary.solid' }}
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit(data);
            }}
          >
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem
          color='state.error'
          fontSize='sm'
          bg='transparent'
          _hover={{ bg: 'state.errorSoft' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TasksList;
